/**
 * A soft pulse while the assistant is thinking, so the silence between the
 * user's phrase and the first spoken word does not feel like a dropped call.
 * Only runs while the microphone is not recording, so it can never be "heard".
 */

export interface ThinkingCue {
  start(): void;
  stop(): void;
}

export interface CueOptions {
  /** Silence before the first pulse: quick answers never trigger it. */
  delayMs?: number;
  intervalMs?: number;
}

export function createThinkingCue(ctx: AudioContext | null, options: CueOptions = {}): ThinkingCue {
  const delayMs = options.delayMs ?? 700;
  const intervalMs = options.intervalMs ?? 1400;
  let delayTimer: ReturnType<typeof setTimeout> | null = null;
  let loopTimer: ReturnType<typeof setInterval> | null = null;

  const pulse = () => {
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(392, now);
    oscillator.frequency.exponentialRampToValueAtTime(330, now + 0.16);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.035, now + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.22);
  };

  const stop = () => {
    if (delayTimer !== null) clearTimeout(delayTimer);
    if (loopTimer !== null) clearInterval(loopTimer);
    delayTimer = null;
    loopTimer = null;
  };

  return {
    start() {
      stop();
      if (!ctx) return;
      delayTimer = setTimeout(() => {
        pulse();
        loopTimer = setInterval(pulse, intervalMs);
      }, delayMs);
    },
    stop,
  };
}
